import { useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { dashboardRoutes } from "../routes/DashboardRoutes";
import {
  FOOTER_SECTION_TAB_LABEL,
  LOWER_SECTION_TAB_LABEL,
  UPPER_SECTION_TAB_LABEL,
} from "../utils/constants";

function mapRouteToSidebarItem(route, { areSubRoutesGroupedSidebarTabs }) {
  return {
    label: route.label,
    header: route.header,
    description: route.description,
    path: route.path,
    icon: route.icon,
    routes: route.routes,
    areSubRoutesGroupedSidebarTabs,
    hideTabs: route.hideTabs || false,
  };
}

export function useDashboardSidebarItems({
  excludeOnboardingSidebar = false,
} = {}) {
  const { user, hasFeature } = useAuth();

  return useMemo(() => {
    const allUpperSectionRoutes = dashboardRoutes
      .filter((route) => route.section === UPPER_SECTION_TAB_LABEL)
      .filter((route) => !route.hideFromSidebar)
      .filter(
        (route) =>
          !(excludeOnboardingSidebar && route.areSubRoutesGroupedSidebarTabs),
      )
      .filter(
        (route) =>
          (!route.allowedUserStatuses ||
            route.allowedUserStatuses.includes(user?.data?.status)) &&
          ((route.feature ? hasFeature(route.feature, null) : true) ||
            !route?.featureProtected),
      )
      .map((route) => {
        const filteredSubs = (route.routes || []).filter(
          (subRoute) =>
            ((subRoute.subFeature
              ? hasFeature(route.feature, subRoute.subFeature)
              : true) ||
              !subRoute?.subFeatureProtected) &&
            (!subRoute.allowedUserStatuses ||
              subRoute.allowedUserStatuses.includes(user?.data?.status)),
        );
        return { ...route, routes: filteredSubs };
      });

    const isAllowed = (route) =>
      (!route.allowedUserStatuses ||
        route.allowedUserStatuses.includes(user?.data?.status)) &&
      ((route.feature ? hasFeature(route.feature, null) : true) ||
        !route?.featureProtected);

    const filteredBottomItems = dashboardRoutes
      .filter((route) => route.section === LOWER_SECTION_TAB_LABEL)
      .filter(isAllowed);

    const footerSidebarItems = dashboardRoutes
      .filter((route) => route.section === FOOTER_SECTION_TAB_LABEL)
      .filter(isAllowed)
      .map((route) => ({
        label: route.label,
        path: route.path,
        icon: route.icon,
      }));

    const filteredMainTabs = allUpperSectionRoutes.filter(
      (route) => !route.areSubRoutesGroupedSidebarTabs,
    );
    const groupedRoutes = allUpperSectionRoutes.filter(
      (route) => route.areSubRoutesGroupedSidebarTabs,
    );

    const sidebarItems = [
      ...filteredMainTabs.map((route) =>
        mapRouteToSidebarItem(route, { areSubRoutesGroupedSidebarTabs: false }),
      ),
      ...groupedRoutes.map((route) =>
        mapRouteToSidebarItem(route, { areSubRoutesGroupedSidebarTabs: true }),
      ),
    ];

    const bottomSidebarItems = filteredBottomItems.map((route) => ({
      label: route.label,
      path: route.path,
      icon: route.icon,
    }));

    return { sidebarItems, bottomSidebarItems, footerSidebarItems };
  }, [user, hasFeature, excludeOnboardingSidebar]);
}
